import React, { useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { FiX, FiSearch } from 'react-icons/fi';
import { useApp } from '../../context/AppContext';
import { useChatThreadBackgroundStyle } from '../../hooks/useChatThreadMessagesAreaStyle';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import PinnedMessages from './PinnedMessages';
import ChatThreadBackgroundPicker from './ChatThreadBackgroundPicker';

const DATE_FILTERS = [
  { value: 'all', label: 'Все время' },
  { value: 'today', label: 'Сегодня' },
  { value: 'yesterday', label: 'Вчера' },
  { value: 'last7', label: 'За 7 дней' },
  { value: 'last30', label: 'За 30 дней' },
];

export default function ChatAreaMobile({ open, onClose, onOpenChatsList }) {
  const chatBgStyle = useChatThreadBackgroundStyle('mobile');
  const { state } = useApp();
  const modalRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');

  // При открытии чата скроллим вниз только если есть сообщения
  useEffect(() => {
    if (open && state.currentChat && messagesContainerRef.current && state.messages.length > 0) {
      // Даем время на рендер, затем скроллим вниз
      const scrollToBottom = () => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      };

      // Первый скролл после небольшой задержки
      const timer1 = setTimeout(scrollToBottom, 100);
      
      // Дополнительные скроллы для гарантии
      const timer2 = setTimeout(scrollToBottom, 300);
      const timer3 = setTimeout(scrollToBottom, 500);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [open, state.currentChat?.id, state.messages.length]);

  // При загрузке/обновлении сообщений также скроллим вниз
  useEffect(() => {
    if (open && state.currentChat && state.messages.length > 0 && messagesContainerRef.current) {
      const timer = setTimeout(() => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          // Скроллим только если пользователь уже внизу или сообщений мало
          const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
          if (isNearBottom || state.messages.length <= 5) {
            container.scrollTop = container.scrollHeight;
          }
        }
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [open, state.messages.length, state.currentChat?.id]);

  const handleClose = () => {
    if (onOpenChatsList) {
      onOpenChatsList();
    }
    onClose();
  };

  if (!open || !state.currentChat) return null;

  // Получаем название чата
  const getChatName = () => {
    const current = state.currentChat;
    if (!current) return 'Мессенджер';
    
    // Для приватных чатов стараемся показать имя собеседника
    if (current.isPrivate || current.type === 'private') {
      const meId = state.user?.id;
      
      // 1) Если имя чата не "Приватный чат", используем его
      if (current.name && current.name !== 'Приватный чат') {
        return current.name;
      }
      
      // 2) Пробуем participants в currentChat
      if (Array.isArray(current.participants)) {
        const other = current.participants.find(p => p.id !== meId);
        if (other) {
          const name = other.username || ((other.first_name || '') + ' ' + (other.last_name || '')).trim();
          if (name) return name;
        }
      }
      
      // 3) Пробуем найти этот же чат в state.chats
      const fromList = (state.chats || []).find(c => c.id === current.id);
      if (fromList && Array.isArray(fromList.participants)) {
        const other = fromList.participants.find(p => p.id !== meId);
        if (other) {
          const name = other.username || ((other.first_name || '') + ' ' + (other.last_name || '')).trim();
          if (name) return name;
        }
      }
      
      // 4) Пробуем взять имя автора последнего сообщения
      const otherMsg = (state.messages || []).slice().reverse().find(m => m.user_id && m.user_id !== meId);
      if (otherMsg && otherMsg.username) return otherMsg.username;
      
      // 5) Фолбэк
      if (state.lastPrivateTarget) {
        const t = state.lastPrivateTarget;
        const name = t.username || ((t.first_name || '') + ' ' + (t.last_name || '')).trim();
        if (name) return name;
      }
      
      return current.name || 'Собеседник';
    }
    
    // Для групп — используем name
    return current.name || 'Чат';
  };

  // Получаем закрепленные сообщения для текущего чата
  const pinnedMessages = state.currentChat ? 
    (state.pinnedMessages[state.currentChat.id] || []) : [];

  const handleScrollToMessage = (messageId) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      messageElement.style.backgroundColor = 'rgba(255, 255, 0, 0.2)';
      setTimeout(() => {
        messageElement.style.backgroundColor = '';
      }, 2000);
    }
  };

  const handleUnpinMessage = (messageId) => {
    if (!window.socket) {
      alert('Нет соединения с сервером!');
      return;
    }
    
    if (!window.confirm('Открепить это сообщение?')) return;
    
    window.socket.emit('unpin_message', { 
      messageId, 
      chatId: state.currentChat?.id 
    });
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        background: 'rgba(0,0,0,0.85)',
        zIndex: 100002, // Выше SidebarNav (100001)
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        touchAction: 'none'
      }}
      onClick={handleClose}
    >
      <div
        ref={modalRef}
        onClick={e => e.stopPropagation()}
        style={{
          ...chatBgStyle,
          transition: 'background-image 0.35s ease, background-color 0.35s ease',
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          maxHeight: '100%',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          color: '#fff',
          overflow: 'hidden',
        }}
      >
        {/* Заголовок с кнопками */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid rgba(67,233,123,0.2)',
          background: 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          flexShrink: 0,
        }}>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(67,233,123,0.15)',
              border: '1px solid #43e97b',
              borderRadius: '50%',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#43e97b',
              fontSize: '20px',
              padding: 0,
              marginRight: '12px',
              flexShrink: 0
            }}
            title="Вернуться к чатам"
          >
            <FaArrowLeft />
          </button>
          <h2 style={{
            margin: 0,
            color: '#43e97b',
            fontWeight: 900,
            fontSize: '1.4em',
            flex: 1,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}          >
            {getChatName()}
          </h2>
          <ChatThreadBackgroundPicker compact />
          <button
            onClick={() => setSearchOpen((v) => !v)}
            style={{
              background: searchOpen ? 'rgba(102, 126, 234, 0.4)' : 'rgba(67,233,123,0.15)',
              border: '1px solid #43e97b',
              borderRadius: '50%',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#43e97b',
              fontSize: '18px',
              padding: 0,
              marginRight: '8px',
              flexShrink: 0
            }}
            title="Поиск по сообщениям"
          >
            <FiSearch />
          </button>
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(67,233,123,0.15)',
              border: '1px solid #43e97b',
              borderRadius: '50%',
              width: 44,
              height: 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#43e97b',
              fontSize: '20px',
              padding: 0,
              marginLeft: '12px',
              flexShrink: 0
            }}
            title="Закрыть"
          >
            <FiX />
          </button>
        </div>

        {/* Панель поиска (мобильная) */}
        {searchOpen && (
          <div className="chat-search-filter" style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            padding: '12px 16px',
            background: 'rgba(15, 23, 42, 0.5)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            borderBottom: '2px solid rgba(109,213,237,0.35)',
            flexShrink: 0,
            position: 'relative',
            zIndex: 9,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
                <FiSearch size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6dd5ed', pointerEvents: 'none' }} />
                <input
                  type="text"
                  placeholder="Поиск по тексту..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    borderRadius: 12,
                    border: '1px solid rgba(102, 126, 234, 0.5)',
                    background: 'rgba(255,255,255,0.12)',
                    color: '#fff',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => { setSearchOpen(false); setSearchQuery(''); setDateFilter('all'); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 12,
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,0.3)',
                  background: 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                  flexShrink: 0
                }}
                title="Закрыть поиск"
              >
                <FiX size={20} />
              </button>
            </div>
            <label style={{ fontSize: '0.9rem', color: '#6dd5ed', marginBottom: 2 }}>Фильтр по дате</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: 12,
                border: '2px solid rgba(109,213,237,0.5)',
                background: '#232931',
                color: '#6dd5ed',
                fontSize: '1rem',
                boxSizing: 'border-box',
                cursor: 'pointer',
                appearance: 'auto',
              }}
              title="Фильтр по дате"
            >
              {DATE_FILTERS.map((f) => (
                <option key={f.value} value={f.value} style={{ background: '#232931', color: '#e0e0e0' }}>{f.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Закрепленные сообщения */}
        <PinnedMessages 
          pinnedMessages={pinnedMessages}
          onScrollToMessage={handleScrollToMessage}
          onUnpinMessage={handleUnpinMessage}
        />

        {/* Список сообщений */}
        <div 
          ref={messagesContainerRef}
          style={{
            flex: 1,
            overflowY: state.messages.length > 0 ? 'auto' : 'hidden',
            WebkitOverflowScrolling: 'touch',
            background: 'transparent',
            minHeight: 0,
            maxHeight: '100%',
            overscrollBehavior: 'contain'
          }}
        >
          <MessageList
            searchQuery={searchOpen ? searchQuery : ''}
            dateFilter={searchOpen ? dateFilter : 'all'}
          />
        </div>

        {/* Индикатор печати */}
        {state.typingUsers.length > 0 && (
          <div style={{
            padding: '8px 20px',
            fontStyle: 'italic',
            color: '#6dd5ed',
            fontSize: '0.95rem',
            background: 'rgba(15, 23, 42, 0.4)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderTop: '1.5px solid rgba(33, 147, 176, 0.4)',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}>
            {state.typingUsers[0]} печатает...
          </div>
        )}

        {/* Поле ввода сообщения */}
        <div style={{
          borderTop: '1px solid rgba(67,233,123,0.1)',
          background: 'rgba(15, 23, 42, 0.55)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          padding: '12px 16px',
          paddingBottom: `calc(70px + env(safe-area-inset-bottom, 0px))`,
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          flexShrink: 0
        }}>
          <MessageInput isMobile={true} />
        </div>

        {/* Подсказка о свайпе */}
        <div style={{
          padding: '8px 20px',
          borderTop: '1px solid rgba(67,233,123,0.1)',
          background: 'rgba(34,40,49,0.6)',
          textAlign: 'center',
          fontSize: '0.75em',
          color: '#b2bec3',
          position: 'sticky',
          bottom: 0,
          zIndex: 19,
          flexShrink: 0,
          display: 'none'
        }}>
          ← Свайпните вправо, чтобы вернуться к чатам
        </div>
      </div>
    </div>,
    document.body
  );
}
